"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { MapPin, Mail, Phone, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section id="contact" ref={sectionRef} className="relative py-16 px-4 md:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
            Get in Touch
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Contact us for reliable equipment leasing solutions for your construction projects.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {}
          <div className={`space-y-6 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {}
            <div className="relative rounded-lg overflow-hidden shadow-medium h-64 lg:h-80 bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1507189/img/construction_equipment_yard_map_view.png" alt="AT & E Location Map" className="w-full h-full object-cover" />
            </div>

            {}
            <div className="space-y-4 p-6 rounded-lg bg-background-dark text-foreground-dark shadow-soft">
              <div className="flex items-start gap-3">
                <MapPin className="size-5 text-primary mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Address</h3>
                  <p className="text-foreground-dark/80">
                    428 Douma Way, Ripon, CA 95366
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="size-5 text-primary mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Email</h3>
                  <Link to="mailto:info@atandellc.com" className="text-foreground-dark/80 hover:text-primary transition-colors duration-[var(--duration-fast)]">
                    info@atandellc.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="size-5 text-primary mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Phone</h3>
                  <p className="text-foreground-dark/80">Contact us via email for inquiries</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-200 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <FormProvider className="p-6 md:p-8 rounded-lg bg-background shadow-soft border border-border" formId="contact-form-main" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              minLength: 10,
              maxLength: 15
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 500
            }
          }} submitText="Send Message" successMessage="Thank you for contacting AT & E! We'll respond to your inquiry shortly." errorMessage="We couldn't send your message. Please try again or contact us directly at info@atandellc.com.">
              <ContactFormFields />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-primary/10 mb-4">
          <Mail className="size-8 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Message Sent!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="hover-lift">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="focus:border-primary transition-colors duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="focus:border-primary transition-colors duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        minLength: {
          value: 10,
          message: "Phone number must be at least 10 digits"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="focus:border-primary transition-colors duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your equipment needs..." className="min-h-32 resize-y focus:border-primary transition-colors duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}